import { Text } from '@byecode/ui'
import { DataSourceType, FilterMode, FilterWay } from '@lighthouse/core'
import {
    getAppointField,
    getIsFindUseInFilter,
    getViewColumns,
    ListItem4ByecodeUi,
    notDataSettingInnerTypes,
    SelectDataSource,
    useAtomAction,
    useAtomData,
    useFindUseObjectContext,
    ViewFieldFilterConfigure
} from '@lighthouse/shared'
import React, { useCallback, useDeferredValue, useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import {
    availableFieldBlocksAtom,
    availableFilterBlocksAtom,
    blockHighlightAtom,
    lastPageOfStackAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily
} from '@/atoms/page/state'
import { FieldSelect } from '@/components/FieldSelect'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDsListAndFieldOptions } from '@/hooks/useDataSource'

import { ListItemMenu } from '../Common/ListItemMenu'
import { CommonOpera } from '../Common/UserOperate'
import { getFieldOptions } from './help'

interface CascadeFilterProps {
    prefixName: string
}

export const CascadeFilter: React.FC<CascadeFilterProps> = ({ prefixName }) => {
    const { control } = useFormContext()
    const currentAppId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const pointer = useWatch({ control, name: `${prefixName}.pointer` })
    const { dataSourceList, fieldOptions, dataSource } = useDsListAndFieldOptions(currentAppId, envId, pointer)
    const selectFieldOptions = getFieldOptions(fieldOptions, dataSource?.schema, FilterWay.textFilter)
    // const excludeDataSourceList = useMemo(
    //     () => dataSourceList.filter(ds => ds.type !== DataSourceType.aggregateDataSource),
    //     [dataSourceList]
    // )

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )
    const pageVariables = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))
    const pageStateParameter = useAtomData(pageStatesParameterFamily(pageId))
    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const findUseObject = useFindUseObjectContext()
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)

    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const { id: dsId, viewOptions } = dataSource ?? {}
    const tableProps = viewOptions?.tableProps ?? []
    const schema = dataSource?.schema ?? {}
    const columns = getViewColumns({
        tableProps,
        schema
    })

    const primaryField = useMemo(() => {
        if (!dataSource) {
            return columns[0]?.fieldId
        }
        const field = getAppointField(dataSource, 'ID')
        return field?.id || columns[0]?.fieldId
    }, [columns, dataSource])

    return (
        <>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>选项来源</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.pointer`}
                    render={({ field: { value, onChange } }) => {
                        return <SelectDataSource withinPortal size="lg" value={value} onChange={onChange} dataSourceList={dataSourceList} />
                    }}
                />
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>选项值</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.fieldPointer`}
                    render={({ field: { value, onChange } }) => {
                        return (
                            <FieldSelect
                                dsId={pointer}
                                style={{
                                    width: 180
                                }}
                                options={selectFieldOptions}
                                value={value}
                                onChange={onChange}
                            />
                        )
                    }}
                />
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>显示为</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.showFieldPointer`}
                    render={({ field: { value, onChange } }) => {
                        return (
                            <FieldSelect
                                dsId={pointer}
                                style={{
                                    width: 180
                                }}
                                options={selectFieldOptions}
                                value={value}
                                onChange={onChange}
                            />
                        )
                    }}
                />
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>父层级</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.parentFieldPointer`}
                    render={({ field: { value, onChange } }) => {
                        return (
                            <FieldSelect
                                dsId={pointer}
                                style={{
                                    width: 180
                                }}
                                options={selectFieldOptions}
                                value={value}
                                onChange={onChange}
                            />
                        )
                    }}
                />
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>排序字段</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.sortFieldPointer`}
                    render={({ field: { value, onChange } }) => {
                        return (
                            <FieldSelect
                                dsId={pointer}
                                style={{
                                    width: 180
                                }}
                                options={selectFieldOptions}
                                value={value}
                                onChange={onChange}
                            />
                        )
                    }}
                />
            </ListItem4ByecodeUi>
            {dataSource && (
                <Controller
                    name={`${prefixName}.filter`}
                    control={control}
                    render={({ field: { onChange, value } }) => {
                        const filter = value
                        const filtersLength = filter?.expression?.conditions?.length || 0
                        const isHighLight = getIsFindUseInFilter({
                            findUseObject,
                            data: filter,
                            currentDsId: dsId
                        })
                        return (
                            <ListItemMenu
                                width="auto"
                                highlighting={isHighLight}
                                enableHover={false}
                                label="筛选数据"
                                positionDependencies={[filter]}
                                value={Boolean(filtersLength) && `${String(filtersLength)} 条筛选项`}
                                popupContent={
                                    <ViewFieldFilterConfigure
                                        dataSource={dataSource}
                                        dataSourceList={dataSourceList}
                                        pagePathParameter={pagePathParameter}
                                        pageQueryParameter={pageQueryParameter}
                                        pageVariables={pageVariables}
                                        pageStateParameter={pageStateParameter}
                                        availableFieldBlocksInfo={availableFieldBlocksInfo}
                                        availableFilterBlocksInfo={availableFilterBlocksInfo}
                                        columns={columns}
                                        primaryField={primaryField}
                                        filter={filter}
                                        paramsMode={FilterMode.CUSTOM}
                                        noSettingInnerType={notDataSettingInnerTypes}
                                        onFilter={onChange}
                                        onMouseEnter={handleMouseEnter}
                                        onMouseLeave={handleMouseLeave}
                                    />
                                }
                            />
                        )
                    }}
                />
            )}
            <Controller
                control={control}
                name={`${prefixName}.isLastLevel`}
                render={({ field }) => <CommonOpera label="必须选到最终层级" value={field.value} onChange={v => field.onChange(v)} />}
            />
        </>
    )
}
